import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ENTERPRISE_CAPABILITIES,
  CAPABILITY_LABELS,
  CAPABILITY_DESCRIPTIONS,
  type EnterpriseCapability,
} from '@/lib/enterprise-capabilities';

interface MemberCapabilityControlsProps {
  partnerId: string;
  userId: string;
  /** Defensive only — the members view lists non-admin members; admins never appear. */
  isAdmin: boolean;
  capabilities: EnterpriseCapability[];
}

/**
 * M1 (#571): per-member capability toggles for one enterprise member row.
 * A full partner admin / super_admin implicitly holds every capability, so we
 * show a badge instead of checkboxes. Otherwise a checkbox per capability
 * reflects the member's current grants; toggling on calls
 * `partner_grant_capability`, off calls `partner_revoke_capability`, then
 * invalidates the partner's capability list so the row re-fetches.
 */
export function MemberCapabilityControls({
  partnerId,
  userId,
  isAdmin,
  capabilities,
}: MemberCapabilityControlsProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<EnterpriseCapability | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      capability,
      grant,
    }: {
      capability: EnterpriseCapability;
      grant: boolean;
    }) => {
      const { error } = await supabase.rpc(
        grant ? 'partner_grant_capability' : 'partner_revoke_capability',
        {
          p_partner_id: partnerId,
          p_grantee_user_id: userId,
          p_capability: capability,
        },
      );
      if (error) throw error;
    },
    onSettled: () => {
      setPending(null);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.enterprisePartners.capabilityList(partnerId),
      });
    },
    onError: (err: unknown, variables) => {
      console.error('Error toggling enterprise capability:', err);
      captureError(err as Error, { feature: 'enterprise-capability-toggle' });
      toast({
        title: 'Error',
        description: `Failed to ${variables.grant ? 'grant' : 'revoke'} ${
          CAPABILITY_LABELS[variables.capability]
        }.`,
        variant: 'destructive',
      });
    },
  });

  if (isAdmin) {
    return (
      <Badge variant="secondary" className="gap-1">
        <ShieldCheck className="h-3 w-3" />
        Admin — all capabilities
      </Badge>
    );
  }

  const granted = new Set(capabilities);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {ENTERPRISE_CAPABILITIES.map((cap) => {
        const checked = granted.has(cap);
        return (
          <label
            key={cap}
            className="flex items-center gap-2 text-sm"
            title={CAPABILITY_DESCRIPTIONS[cap]}
          >
            <Checkbox
              aria-label={CAPABILITY_LABELS[cap]}
              checked={checked}
              disabled={mutation.isPending}
              onCheckedChange={(next) => {
                setPending(cap);
                mutation.mutate({ capability: cap, grant: next === true });
              }}
            />
            <span className={pending === cap ? 'opacity-60' : undefined}>
              {CAPABILITY_LABELS[cap]}
            </span>
          </label>
        );
      })}
    </div>
  );
}
